import React, { useState } from 'react';
import './GastosTable.css';

const MONTHS = ['Jan','Fev','Mar','Abr','Mai','Jun','Jul','Ago','Set','Out','Nov','Dez'];
const fmt = v => (v || 0).toLocaleString('pt-BR', { style:'currency', currency:'BRL' });
const TIPO_LABELS = { cartao: '💳 Cartão', fixo: '📌 Fixo', outro: '🗂 Outro' };

function EditableCell({ value, tipoLancamento, onChange, onToggleProjecao, isProjecao, modoProjecao, hideValues = false, showTipoToggle = false }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState('');
  const [tipo, setTipo] = useState(tipoLancamento || 'saida');

  function handleClick() {
    if (modoProjecao) {
      if (value > 0) onToggleProjecao();
    } else {
      setVal(value > 0 ? value.toString() : '');
      setTipo(tipoLancamento || 'saida');
      setEditing(true);
    }
  }

  function commit() {
    onChange(parseFloat(val) || 0, tipo);
    setEditing(false);
  }

  if (editing) return (
    <div className="cell-edit-wrap">
      {showTipoToggle && (
        <div className="cell-tipo-toggle">
          <button type="button" className={`tipo-mini-btn entrada ${tipo === 'entrada' ? 'active' : ''}`} onClick={() => setTipo('entrada')}>↓</button>
          <button type="button" className={`tipo-mini-btn saida ${tipo === 'saida' ? 'active' : ''}`} onClick={() => setTipo('saida')}>↑</button>
        </div>
      )}
      <input
        type="number"
        step="0.01"
        className="cell-input"
        value={val}
        onChange={e => setVal(e.target.value)}
        onBlur={commit}
        onKeyDown={e => { if (e.key === 'Enter') commit(); if (e.key === 'Escape') setEditing(false); }}
        autoFocus
      />
    </div>
  );

  const corClasse = value > 0 && showTipoToggle
    ? (tipoLancamento === 'entrada' ? 'is-entrada' : 'is-saida')
    : '';

  return (
    <span
      className={`cell-val ${value > 0 ? 'has-value' : 'empty'} ${corClasse} ${isProjecao ? 'projecao' : ''} ${modoProjecao && value > 0 ? 'modo-projecao-hover' : ''}`}
      onClick={handleClick}
      title={modoProjecao
        ? (isProjecao ? 'Clique para remover projeção' : 'Clique para marcar como projeção')
        : 'Clique para editar'
      }
    >
      {value > 0 ? (hideValues ? '••••••' : fmt(value)) : '—'}
      {isProjecao && <span className="projecao-tag">P</span>}
    </span>
  );
}

export default function GastosTable({ contas, lancamentos, metas, ano, mesSel, onValorChange, onToggleProjecao, onMetaChange, onEditConta, onDeleteConta, tiposCustom = [], hideValues = false }) {
  const mask = v => hideValues ? '••••••' : fmt(v);
  const [expandedTipos, setExpandedTipos] = useState({ cartao: true, fixo: true, outro: true });
  const [filterTipo, setFilterTipo] = useState('todos');
  const [modoProjecao, setModoProjecao] = useState(false);

  function getLancamento(contaId, mes) {
    return lancamentos.find(l => l.conta_id === contaId && l.mes === mes + 1);
  }

  // Valor para os totais de GASTO (TOTAL/LIMITE/DIFERENÇA) — só conta saídas, exclui projeção
  function getLancamentoGasto(contaId, mes) {
    const l = getLancamento(contaId, mes);
    if (!l || l.is_projecao) return 0;
    if (l.tipo_lancamento === 'entrada') return 0;
    return l.valor || 0;
  }

  function getLancamentoDisplay(contaId, mes) {
    return getLancamento(contaId, mes)?.valor || 0;
  }

  function isProjecao(contaId, mes) {
    return getLancamento(contaId, mes)?.is_projecao || false;
  }

  function getMeta(mes) {
    return metas.find(m => m.mes === mes + 1)?.valor_referencia || 0;
  }

  // Saldo do mês: entradas - saídas (excluindo projeção), considerando TODAS as contas
  function getSaldoMes(mes) {
    let entradas = 0, saidas = 0;
    contas.forEach(c => {
      const l = getLancamento(c.id, mes);
      if (!l || l.is_projecao) return;
      if (l.tipo_lancamento === 'entrada') entradas += l.valor || 0;
      else saidas += l.valor || 0;
    });
    return entradas - saidas;
  }

  // Valor total em projeção do mês (com sinal)
  function getProjecaoMes(mes) {
    let total = 0;
    contas.forEach(c => {
      const l = getLancamento(c.id, mes);
      if (!l || !l.is_projecao) return;
      total += l.tipo_lancamento === 'entrada' ? (l.valor || 0) : -(l.valor || 0);
    });
    return total;
  }

  const tiposCustomFormatted = tiposCustom.map(t => ({
    value: `custom_${t.id}`,
    label: `${t.icone} ${t.nome}`,
  }));
  const TIPO_LABELS_ALL = {
    ...TIPO_LABELS,
    ...Object.fromEntries(tiposCustomFormatted.map(t => [t.value, t.label]))
  };
  const tiposCustomComContas = tiposCustomFormatted.filter(t => {
    const customId = t.value.replace('custom_', '');
    return contas.some(c => c.tipo === 'custom' && c.tipo_custom_id === customId);
  });
  const tipoGroups = ['cartao', 'fixo', 'outro', ...tiposCustomComContas.map(t => t.value)];
  const visibleTipos = filterTipo === 'todos' ? tipoGroups : [filterTipo];

  return (
    <div className="gastos-table-wrap">
      <div className="table-toolbar">
        <div className="filter-btns">
          {['todos', 'cartao', 'fixo', 'outro', ...tiposCustomComContas.map(t => t.value)].map(t => (
            <button
              key={t}
              className={`btn btn-sm ${filterTipo === t ? 'btn-primary' : 'btn-ghost'}`}
              onClick={() => setFilterTipo(t)}
            >
              {t === 'todos' ? 'Todos' : TIPO_LABELS_ALL[t]}
            </button>
          ))}
        </div>

        <div style={{ display:'flex', alignItems:'center', gap:8 }}>
          {modoProjecao && (
            <span className="projecao-aviso">🔴 Modo Projeção ativo — clique nas células para alternar</span>
          )}
          <button
            className={`btn btn-sm ${modoProjecao ? 'btn-danger' : 'btn-ghost'}`}
            onClick={() => setModoProjecao(m => !m)}
            title="Ativar modo projeção — valores marcados não entram no total"
          >
            🔴 {modoProjecao ? 'Sair da Projeção' : 'Marcar Projeção'}
          </button>
        </div>
      </div>

      {modoProjecao && (
        <div className="projecao-banner">
          <strong>Modo Projeção:</strong> clique em qualquer célula com valor para marcar/desmarcar como projeção.
        </div>
      )}

      {!modoProjecao && (
        <div className="tipo-legenda">
          <span className="legenda-item"><span className="legenda-dot entrada" /> Entrada</span>
          <span className="legenda-item"><span className="legenda-dot saida" /> Saída</span>
          <span className="text-xs text-muted">Ao editar uma célula, escolha se é entrada ou saída</span>
        </div>
      )}

      <div className="table-scroll">
        <table className="gastos-table">
          <thead>
            <tr>
              <th className="col-conta">Conta</th>
              <th className="col-fech">Fechamento</th>
              <th className="col-venc">Vencimento</th>
              {MONTHS.map((m, i) => (
                <th key={i} className={`col-mes ${i === mesSel ? 'mes-ativo' : ''}`}>{m}</th>
              ))}
              <th className="col-acoes">Ações</th>
            </tr>
          </thead>
          <tbody>
            {visibleTipos.map(tipo => {
              const contasTipo = contas.filter(c => {
                if (tipo.startsWith('custom_')) {
                  const customId = tipo.replace('custom_', '');
                  return c.tipo === 'custom' && c.tipo_custom_id === customId;
                }
                return c.tipo === tipo;
              });
              if (!contasTipo.length) return null;
              const expanded = expandedTipos[tipo];

              return (
                <React.Fragment key={tipo}>
                  <tr className="section-row" onClick={() => setExpandedTipos(p => ({ ...p, [tipo]: !p[tipo] }))}>
                    <td colSpan={3} className="section-title">
                      <span className="toggle-icon">{expanded ? '▾' : '▸'}</span>
                      {TIPO_LABELS_ALL[tipo]}
                      <span className="section-count">{contasTipo.length}</span>
                    </td>
                    {MONTHS.map((_, i) => (
                      <td key={i} className={`section-sum ${i === mesSel ? 'mes-ativo' : ''}`}>
                        {expanded ? '' : (
                          <span className="font-mono text-xs">
                            {contasTipo.reduce((acc, c) => acc + getLancamentoGasto(c.id, i), 0) > 0
                              ? mask(contasTipo.reduce((acc, c) => acc + getLancamentoGasto(c.id, i), 0))
                              : '—'}
                          </span>
                        )}
                      </td>
                    ))}
                    <td />
                  </tr>

                  {expanded && contasTipo.map(conta => (
                    <tr key={conta.id} className="conta-row">
                      <td className="col-conta">
                        <span className="conta-nome">{conta.nome}</span>
                      </td>
                      <td className="col-fech">
                        {conta.fechamento_dia ? (
                          <span className="fech-badge">{String(conta.fechamento_dia).padStart(2,'0')}/mês</span>
                        ) : '—'}
                      </td>
                      <td className="col-venc">
                        {conta.vencimento_dia ? (
                          <span className="fech-badge venc">{String(conta.vencimento_dia).padStart(2,'0')}/mês</span>
                        ) : '—'}
                      </td>
                      {MONTHS.map((_, i) => {
                        const l = getLancamento(conta.id, i);
                        return (
                          <td key={i} className={`col-mes ${i === mesSel ? 'mes-ativo' : ''}`}>
                            <EditableCell
                              value={getLancamentoDisplay(conta.id, i)}
                              tipoLancamento={l?.tipo_lancamento}
                              onChange={(val, tipo) => onValorChange(conta.id, i, val, tipo)}
                              onToggleProjecao={() => onToggleProjecao(conta.id, i)}
                              isProjecao={isProjecao(conta.id, i)}
                              modoProjecao={modoProjecao}
                              hideValues={hideValues}
                              showTipoToggle={true}
                            />
                          </td>
                        );
                      })}
                      <td className="col-acoes">
                        <div className="acoes-btns">
                          <button className="acao-btn edit" onClick={() => onEditConta(conta)} title="Editar">✎</button>
                          <button className="acao-btn del" onClick={() => onDeleteConta(conta.id)} title="Excluir">✕</button>
                        </div>
                      </td>
                    </tr>
                  ))}

                  {expanded && contasTipo.length > 1 && (
                    <tr className="subtotal-row">
                      <td colSpan={3} className="subtotal-label">Subtotal {TIPO_LABELS_ALL[tipo]}</td>
                      {MONTHS.map((_, i) => {
                        const sub = contasTipo.reduce((acc, c) => acc + getLancamentoGasto(c.id, i), 0);
                        return (
                          <td key={i} className={`subtotal-val font-mono ${i === mesSel ? 'mes-ativo' : ''}`}>
                            {sub > 0 ? mask(sub) : '—'}
                          </td>
                        );
                      })}
                      <td />
                    </tr>
                  )}
                </React.Fragment>
              );
            })}

            <tr className="total-row">
              <td colSpan={3} className="total-label">TOTAL</td>
              {MONTHS.map((_, i) => {
                const tot = contas.reduce((acc, c) => acc + getLancamentoGasto(c.id, i), 0);
                return (
                  <td key={i} className={`total-val font-mono ${i === mesSel ? 'mes-ativo' : ''}`}>
                    {tot > 0 ? mask(tot) : '—'}
                  </td>
                );
              })}
              <td />
            </tr>

            <tr className="meta-row">
              <td colSpan={3} className="meta-label">LIMITE MENSAL</td>
              {MONTHS.map((_, i) => {
                const m = getMeta(i);
                return (
                  <td key={i} className={`meta-val ${i === mesSel ? 'mes-ativo' : ''}`}>
                    <EditableCell value={m} onChange={val => onMetaChange(i, val)} onToggleProjecao={() => {}} isProjecao={false} modoProjecao={false} hideValues={hideValues} showTipoToggle={false} />
                  </td>
                );
              })}
              <td />
            </tr>

            <tr className="dif-row">
              <td colSpan={3} className="dif-label">DIFERENÇA DO LIMITE</td>
              {MONTHS.map((_, i) => {
                const tot = contas.reduce((acc, c) => acc + getLancamentoGasto(c.id, i), 0);
                const m = getMeta(i);
                const dif = m - tot;
                return (
                  <td key={i} className={`dif-val font-mono ${i === mesSel ? 'mes-ativo' : ''} ${dif >= 0 ? 'pos' : 'neg'}`}>
                    {m > 0 || tot > 0 ? (hideValues ? '••••••' : `${dif >= 0 ? '+' : ''}${fmt(dif)}`) : '—'}
                  </td>
                );
              })}
              <td />
            </tr>

            <tr className="saldo-proj-row">
              <td colSpan={3} className="saldo-proj-label">
                SALDO + PROJEÇÃO
                <span className="text-xs" style={{ fontWeight:400, display:'block', color:'var(--text3)', textTransform:'none', letterSpacing:0 }}>
                  entradas − saídas + projeções do mês
                </span>
              </td>
              {MONTHS.map((_, i) => {
                const saldo = getSaldoMes(i) + getProjecaoMes(i);
                return (
                  <td key={i} className={`saldo-proj-val font-mono ${i === mesSel ? 'mes-ativo' : ''} ${saldo >= 0 ? 'pos' : 'neg'}`}>
                    {hideValues ? '••••••' : `${saldo >= 0 ? '+' : ''}${fmt(saldo)}`}
                  </td>
                );
              })}
              <td />
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}
